import * as React from 'react';
import { useEffect } from 'react';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import { useLocation } from 'js/lib/useRouter';
import user from 'js/lib/user';

import PrePurchaseUpsellS12nEnrollModal from 'bundles/coursera-plus-common/components/elevateLTV/prePurchaseUpsell/PrePurchaseUpsellS12nEnrollModal';
import IdealUpgradeEnrollModalContainer from 'bundles/coursera-plus-common/components/subscriptionTiers/upgradeToPlus/IdealUpgradeEnrollModalContainer';
import IndiaPriceTestUpgradeModal from 'bundles/coursera-plus-common/components/subscriptionTiers/upgradeToPlus/IndiaUpgrade/IndiaPriceTestUpgradeModal';
import { showPrePurchaseUpsellFlow } from 'bundles/coursera-plus-common/utils/prePurchaseUpsellUtils';
import { getIsLearnerImpressedInIndiaTiersEpic } from 'bundles/coursera-plus-common/utils/subscriptionTiersUtils';
import {
  canEnrollThroughFreemium,
  isFreemiumModelRolloutGAEnabled,
} from 'bundles/enroll-common/utils/freemiumModelUtils';
import {
  generateImpressionForMixAndMatchSkipSelectS12nModal,
  previewIsMixAndMatchSkipSelectS12nModalEnabled,
} from 'bundles/enroll-common/utils/mixAndMatchUtils';
import CourseraPlusEnrollModal from 'bundles/enroll-modal/components/coursera-plus/CourseraPlusEnrollModal';
import GuidedProjectEnrollModal from 'bundles/enroll-modal/components/guided-project/GuidedProjectEnrollModal';
import CourseEnrollS12nSelectionModal from 'bundles/enroll-modal/components/mix-and-match/CourseEnrollS12nSelectionModal';
import CourseEnrollS12nSelectionModalV2 from 'bundles/enroll-modal/components/mix-and-match/CourseEnrollS12nSelectionModalV2';
import NonRecurringEnrollModal from 'bundles/enroll-modal/components/non-recurring/NonRecurringEnrollModal';
import NonRecurringEnrollModalV2 from 'bundles/enroll-modal/components/non-recurring/NonRecurringEnrollModalV2';
import S12nPaymentMethodEnrollModal from 'bundles/enroll-modal/components/non-recurring/S12nPaymentMethodEnrollModal';
import CancelPreEnrollS12nSelectModal from 'bundles/enroll-modal/components/pre-enrollment/CancelPreEnrollS12nSelectModal';
import S12nPreEnrollModal from 'bundles/enroll-modal/components/pre-enrollment/S12nPreEnrollModal';
import SelfPacedProjectEnrollModal from 'bundles/enroll-modal/components/projects/SelfPacedProjectEnrollModal';
import ClosedCourseEnrollModal from 'bundles/enroll-modal/components/standalone-course/ClosedCourseEnrollModal';
import ClosedCourseEnrollModalV2 from 'bundles/enroll-modal/components/standalone-course/ClosedCourseEnrollModalV2';
import CourseWithFullDiscountEnrollModal from 'bundles/enroll-modal/components/standalone-course/CourseWithFullDiscountEnrollModal';
import StandaloneCourseEnrollModal from 'bundles/enroll-modal/components/standalone-course/StandaloneCourseEnrollModal';
import StandaloneCourseEnrollModalV2 from 'bundles/enroll-modal/components/standalone-course/StandaloneCourseEnrollModalV2';
import StandaloneCourseEnrollModalV3 from 'bundles/enroll-modal/components/standalone-course/StandaloneCourseEnrollModalV3';
import SubscriptionEnrollModal from 'bundles/enroll-modal/components/subscriptions/SubscriptionEnrollModal';
import SubscriptionEnrollModalV2 from 'bundles/enroll-modal/components/subscriptions/SubscriptionEnrollModalV2';
import UserInterestModal from 'bundles/enroll-modal/components/user-interest/UserInterestModal';
import { useCourseEnrollModalData } from 'bundles/enroll-modal/data/usePageData';
import EnterpriseEnrollmentChoiceModalForXDP from 'bundles/enterprise-legacy-learner-home/components/EnterpriseEnrollmentChoiceModalForXDP';
import { PRODUCT_TYPES } from 'bundles/enterprise-legacy-learner-home/constants/ProgramActionConstants';
import GoogleCertificateEpicClient from 'bundles/epic/clients/GoogleCertificate';
import paymentsExperiments from 'bundles/epic/clients/payments';
import { CourseType } from 'bundles/naptimejs/resources/courseTypeMetadata.v1';
import { useCoursePageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffCourseContext';

export type PropsFromCaller = {
  courseId: string;
  s12nId?: string;
  partnerIds?: Array<string>;
  onClose: (event?: React.MouseEvent<HTMLElement>) => void;
  onS12nSelect: (s12nId: string) => void;
  // Pre Enrollment V2
  fromMultipleLearningProgramsLink?: boolean;
  router?: InjectedRouter;
  isPrePurchaseUpsellEligible?: boolean;
};

const CourseEnrollModal = ({
  courseId,
  onClose,
  onS12nSelect,
  fromMultipleLearningProgramsLink,
  isPrePurchaseUpsellEligible,
}: PropsFromCaller) => {
  const {
    course,
    s12n,
    enrollmentAvailableChoices,
    courseTypeMetadataWithVersion,
    s12nDerivatives,
    s12nSubscriptionOwnableProduct,
    programs,
    thirdPartyOrganizations,
    invitedPrograms,
    invitedThirdPartyOrganizations,
    ownsCourseraLite,
    isCourseraPlusEligibleProduct,
    promotionEligibilities,
  } = useCourseEnrollModalData();

  const { query } = useLocation();
  const pageData = useCoursePageData();
  const s12nSlugFromUrl = query?.specialization;
  const selectedS12nFromUrl = pageData?.specializations?.find((s12nItem) => s12nItem.slug === s12nSlugFromUrl);
  const preselectedS12nId = selectedS12nFromUrl?.id;

  const {
    canSubscribeToS12n,
    canEnrollThroughS12nPrepaid,
    canEnrollThroughCourseraPlus,
    didPurchase,
    isSpecializationUpgradeRequired,
    canEnrollThroughProgram,
    canEnrollCourseWithFullDiscount,
    canEnrollThroughProgramInvitation,
    canSubscribeToCourseraPlus,
    canPurchaseSingleCourse,
    canPurchaseThroughCourseraPlus,
    isEnrolled,
    isMixAndMatch,
    isPreEnrolled,
    preEnrolledS12nIds,
    selectedS12nIdOverrideToEnrollThroughCourseraPlus: selectedS12nIdOverride,
    isMixMatchWithS12nsThatCannotEnrollThroughPlus,
    canEnrollThroughPlusWithSelectedS12n,
  } = enrollmentAvailableChoices;
  const isEligibleForMixAndMatchSkipSelectS12nModal =
    Boolean(preselectedS12nId) && isMixAndMatch && (canSubscribeToS12n || canEnrollThroughS12nPrepaid);
  const isEligibleForS12nSelectionCTA =
    isMixAndMatch && !s12nSlugFromUrl && (canSubscribeToS12n || canEnrollThroughS12nPrepaid);
  const isMixAndMatchSkipSelectS12nModalEnabled = previewIsMixAndMatchSkipSelectS12nModalEnabled({
    isEligible: isEligibleForMixAndMatchSkipSelectS12nModal,
  });

  useEffect(() => {
    if (isEligibleForMixAndMatchSkipSelectS12nModal && preselectedS12nId && isMixAndMatchSkipSelectS12nModalEnabled) {
      onS12nSelect(preselectedS12nId);
    }
  }, [
    isEligibleForMixAndMatchSkipSelectS12nModal,
    preselectedS12nId,
    onS12nSelect,
    isMixAndMatchSkipSelectS12nModalEnabled,
  ]);

  const isMultiplePreEnrolledS12ns = preEnrolledS12nIds.length > 1;
  const showPreEnrollCancelModal = isMixAndMatch && isPreEnrolled && !fromMultipleLearningProgramsLink;
  const isEnterpriseUser = canEnrollThroughProgram || canEnrollThroughProgramInvitation;
  const hasPromotion = Boolean(promotionEligibilities?.isEligible ? promotionEligibilities?.promoCodeId : undefined);
  const { isClosedCourse } = course;
  const isFreemiumLearner = canEnrollThroughFreemium({
    courseId: course.id,
    enrollmentAvailableChoices,
    courseTypeMetadataWithVersion,
  });

  useEffect(() => {
    if (showPreEnrollCancelModal && !isMultiplePreEnrolledS12ns) {
      // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      onS12nSelect(preEnrolledS12nIds?.[0]);
    }
  }, [showPreEnrollCancelModal, isMultiplePreEnrolledS12ns, preEnrolledS12nIds, onS12nSelect]);

  useEffect(() => {
    if (isMixAndMatch && selectedS12nIdOverride) {
      onS12nSelect(selectedS12nIdOverride);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedS12nIdOverride, isMixAndMatch]);

  useEffect(() => {
    if (isEligibleForS12nSelectionCTA || isEligibleForMixAndMatchSkipSelectS12nModal) {
      generateImpressionForMixAndMatchSkipSelectS12nModal({
        isEligibleForS12nSelectionCTA,
        isEligibleForMixAndMatchSkipSelectS12nModal,
      });
    }
  }, [isEligibleForS12nSelectionCTA, isEligibleForMixAndMatchSkipSelectS12nModal]);

  if (showPreEnrollCancelModal && isMultiplePreEnrolledS12ns) {
    return (
      <CancelPreEnrollS12nSelectModal
        handleClose={onClose}
        automaticallyEnroll={isEnterpriseUser || canEnrollThroughCourseraPlus}
      />
    );
  }

  // If user can enroll into multiple s12ns, have user first select a specific s12n to enroll in
  if (isMixAndMatch && !s12n) {
    if (isEligibleForMixAndMatchSkipSelectS12nModal && isMixAndMatchSkipSelectS12nModalEnabled) {
      // if user state is eligible for mix and match skip select s12n modal, and the modal is enabled, show null
      // while waiting for the s12n to be selected
      return null;
    }
    return isFreemiumLearner || isEnrolled || showPrePurchaseUpsellFlow(location.pathname) ? (
      <CourseEnrollS12nSelectionModalV2
        onClose={onClose}
        onSubmit={onS12nSelect}
        isTerminal={canEnrollThroughCourseraPlus}
      />
    ) : (
      <CourseEnrollS12nSelectionModal
        onClose={onClose}
        onSubmit={onS12nSelect}
        isTerminal={canEnrollThroughCourseraPlus}
      />
    );
  }

  const waitForBothProgramDataFetch = canEnrollThroughProgram && canEnrollThroughProgramInvitation;

  const isFromS12nSelection = isMixAndMatch && Boolean(s12n);
  const s12nId = s12n?.id;
  const shouldShowPrepaidModal =
    s12nId &&
    ((canEnrollThroughS12nPrepaid && enrollmentAvailableChoices?.prepaidEnrollmentS12nIds?.includes(s12nId)) ||
      (canPurchaseThroughCourseraPlus &&
        enrollmentAvailableChoices?.courseraPlusPrepaidEnrollmentS12nIds?.includes(s12nId)));

  const shouldShowPreEnrollModal =
    !paymentsExperiments.preview('disablePreEnrollment') &&
    s12nId &&
    enrollmentAvailableChoices.canPreEnrollOrIsPreEnrolledS12n(s12nId);

  // For the list of s12ns that are in the pre enrolled flow, show the pre enroll modal before any other s12n modal
  if (shouldShowPreEnrollModal && s12nId) {
    return (
      <S12nPreEnrollModal
        s12nId={s12nId}
        onSdp={false}
        onClose={onClose}
        courseId={courseId}
        automaticallyEnroll={isEnterpriseUser || canEnrollThroughCourseraPlus}
      />
    );
    // Show the product interest modal if the course is not launched and learner can't subscribe to the course's s12n
  } else if (
    (s12nDerivatives?.isSubscription || s12nSubscriptionOwnableProduct?.isSubscription) &&
    !course.isLaunched &&
    !canSubscribeToS12n
  ) {
    return <UserInterestModal courseId={courseId} onClose={onClose} />;
    // Only trigger Coursera Plus flow for users who are already subscribed users
  } else if (
    canEnrollThroughCourseraPlus &&
    (!isMixMatchWithS12nsThatCannotEnrollThroughPlus || canEnrollThroughPlusWithSelectedS12n(s12nId as string))
  ) {
    return <CourseraPlusEnrollModal courseId={courseId} onClose={onClose} />;
  } else if (
    !hasPromotion &&
    canSubscribeToS12n &&
    canSubscribeToCourseraPlus &&
    showPrePurchaseUpsellFlow(location.pathname)
  ) {
    return (
      <PrePurchaseUpsellS12nEnrollModal handleClose={onClose} onSdp={false} isFromS12nSelection={isFromS12nSelection} />
    );
  } else if (isCourseraPlusEligibleProduct && isEnrolled && ownsCourseraLite) {
    if (getIsLearnerImpressedInIndiaTiersEpic()) {
      return <IndiaPriceTestUpgradeModal onClose={onClose} />;
    }
    return <IdealUpgradeEnrollModalContainer onClose={onClose} />;
  } else if (shouldShowPrepaidModal) {
    // Recording impression for India price test.
    // Epic: https://tools.coursera.org/epic/experiment/WS16jhbwEfCHlw4giApofQ
    GoogleCertificateEpicClient.get('india-price-decrease');
    let PrepaidModal = null;
    if (enrollmentAvailableChoices?.subscriptionEnrollmentS12nIds.includes(s12nId)) {
      PrepaidModal = S12nPaymentMethodEnrollModal;
    } else if (isFreemiumModelRolloutGAEnabled()) {
      PrepaidModal = NonRecurringEnrollModalV2;
    } else {
      PrepaidModal = NonRecurringEnrollModal;
    }

    return (
      <PrepaidModal
        s12nId={s12nId}
        courseId={course.id}
        onSdp={false}
        onClose={onClose}
        isFromS12nSelection={isFromS12nSelection}
      />
    );
  } else if (canSubscribeToS12n) {
    if (isFreemiumLearner) {
      return <SubscriptionEnrollModalV2 onClose={onClose} onSdp={false} isFromS12nSelection={isFromS12nSelection} />;
    }
    return <SubscriptionEnrollModal onClose={onClose} onSdp={false} isFromS12nSelection={isFromS12nSelection} />;
  } else if (canEnrollCourseWithFullDiscount) {
    return <CourseWithFullDiscountEnrollModal courseId={courseId} onClose={onClose} />;
  }
  // Technically Guided Project is a closed course. We don't want to show the Closed course modal for this so
  // the Guided Project check should be placed before closed course.
  else if (
    courseTypeMetadataWithVersion?.courseTypeMetadata.typeName === CourseType.RHYME_PROJECT &&
    canPurchaseSingleCourse
  ) {
    return <GuidedProjectEnrollModal courseId={courseId} onClose={onClose} />;
  } else if (
    courseTypeMetadataWithVersion?.courseTypeMetadata.typeName === CourseType.PROJECT &&
    canPurchaseSingleCourse
  ) {
    return <SelfPacedProjectEnrollModal courseId={courseId} onClose={onClose} />;
  } else if (isClosedCourse && !didPurchase && !canEnrollThroughProgram && !isSpecializationUpgradeRequired) {
    return !hasPromotion && showPrePurchaseUpsellFlow(location.pathname) && isCourseraPlusEligibleProduct ? (
      <ClosedCourseEnrollModalV2 onClose={onClose} />
    ) : (
      <ClosedCourseEnrollModal onClose={onClose} />
    );
  } else if (isEnterpriseUser) {
    return (
      <EnterpriseEnrollmentChoiceModalForXDP
        thirdPartyOrganizations={thirdPartyOrganizations}
        programs={programs}
        invitedThirdPartyOrganizations={invitedThirdPartyOrganizations}
        invitedPrograms={invitedPrograms}
        productId={course.id}
        productType={PRODUCT_TYPES.COURSE}
        userId={user.get().id}
        handleClose={onClose}
        waitForBothProgramDataFetch={waitForBothProgramDataFetch}
      />
    );
    // This modal both handles standalone course enrollment
    // and enrolling into a course as part of an existing s12n subscription/purchase
    // TODO: separate out these two cases being handled into different components
  } else {
    // For freemium model flow
    if (isFreemiumLearner) {
      return (
        <StandaloneCourseEnrollModalV3
          s12nId={s12nId}
          onClose={onClose}
          isPrePurchaseUpsellEligible={isPrePurchaseUpsellEligible}
        />
      );
    }

    // For the prepurchase upsell enrollment flow
    // C+ eligible Standalone courses
    return !hasPromotion && showPrePurchaseUpsellFlow(location.pathname) && isCourseraPlusEligibleProduct ? (
      <StandaloneCourseEnrollModalV2 s12nId={s12nId} onClose={onClose} />
    ) : (
      <StandaloneCourseEnrollModal courseId={courseId} s12nId={s12nId} onClose={onClose} />
    );
  }
};

export default CourseEnrollModal;
